import {User} from '@ui/types/user';
import {Price} from './price';
import {Product} from './product';

export interface Subscription {
  id: number;
  price_id: number;
  product_id: number;
  user_id: number;
  purpose?: string | null;
  on_grace_period?: boolean;
  gateway_name: 'stripe' | 'paypal' | 'pix' | 'google_play' | 'family' | 'free' | 'none';
  gateway_provider?: 'sicredi' | 'starkbank' | string | null;
  gateway_id: string;
  gateway_status: string;
  gateway_payload?: Record<string, any> | null;
  valid?: boolean;
  past_due?: boolean;
  active?: boolean;
  cancelled?: boolean;
  on_trial?: boolean;
  price?: Price & {
    product_id?: number;
    product?: Product & {purpose?: string | null};
  };
  product?: Product & {purpose?: string | null};
  trial_ends_at: string;
  ends_at: string;
  updated_at: string;
  created_at: string;
  description: string;
  renews_at: string;
  user?: User;
}

// Missing purpose is the legacy platform contract. Every supplied snapshot
// must agree; the full subscriptions collection also contains channel clubs.
export function isPlatformSubscription(
  subscription: Omit<Subscription, 'user'>,
): boolean {
  return (
    (subscription.purpose ?? 'platform') === 'platform' &&
    (subscription.product?.purpose ?? 'platform') === 'platform' &&
    (subscription.price?.product?.purpose ?? 'platform') === 'platform' &&
    (subscription.product == null ||
      subscription.product.id === subscription.product_id) &&
    (subscription.price == null ||
      subscription.price.id === subscription.price_id) &&
    (subscription.price?.product_id == null ||
      subscription.price.product_id === subscription.product_id) &&
    (subscription.price?.product == null ||
      subscription.price.product.id === subscription.product_id)
  );
}

export function selectCurrentSubscription(
  subscriptions: Omit<Subscription, 'user'>[],
): Omit<Subscription, 'user'> | undefined {
  return subscriptions.filter(isPlatformSubscription).sort((a, b) => {
    const scoreDiff = subscriptionPriority(b) - subscriptionPriority(a);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    return comparableDate(b) - comparableDate(a) || b.id - a.id;
  })[0];
}

function subscriptionPriority(subscription: Omit<Subscription, 'user'>): number {
  if (subscription.active || subscription.valid) return 100;
  if (subscription.on_grace_period) return 80;
  if (subscription.gateway_status === 'pending') return 60;
  if (subscription.cancelled) return 20;
  return 10;
}

function comparableDate(subscription: Omit<Subscription, 'user'>): number {
  return new Date(
    subscription.updated_at || subscription.created_at || subscription.renews_at || 0,
  ).getTime();
}
